import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { ShoppingCart, Truck, Shield, Star, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { WhyChooseUs } from '../components/WhyChooseUs';
import { Reviews } from '../components/Reviews';
import { FestiveBanner } from '../components/FestiveBanner';
import { WhatsAppOrderButton } from '../components/WhatsAppOrderButton';

export const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    Promise.all([
      api.get('/categories'),
      api.get('/products?featured=true&limit=8'),
      api.get('/reviews/stats'),
    ])
      .then(([c, p, s]) => {
        setCategories(c.data);
        setFeatured(p.data.products);
        setReviewStats(s.data);
      })
      .finally(() => setLoading(false));
  }, []);

  // AggregateRating JSON-LD for the Organization (Google rich snippets)
  const aggregateJsonLd =
    reviewStats && reviewStats.count > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Sanatanbhavan',
          url: 'https://sanatanbhavan.com',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(reviewStats.average),
            reviewCount: String(reviewStats.count),
            bestRating: '5',
            worstRating: '1',
          },
        }
      : null;

  return (
    <div>
      <SEO
        title="Authentic Puja Samagri Online — Diyas, Idols, Rudraksha"
        description="Shop authentic Puja Samagri online at Sanatanbhavan: brass diyas, copper kalash, rudraksha malas, hawan kund, festival puja kits & more. Free shipping above ₹500. Pan-India delivery."
        jsonLd={aggregateJsonLd}
      />
      <FestiveBanner />
      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-100 via-yellow-50 to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-orange-800 mb-4">🙏 Welcome to<br/><span className="text-orange-600">Sanatanbhavan</span></h1>
            <p className="text-lg text-gray-700 mb-6">Your one-stop destination for authentic Puja Samagri, Idols, Yantras, and spiritual items.</p>
            <div className="flex gap-4">
              <Link to="/products"><Button size="lg" className="bg-orange-600 hover:bg-orange-700">Shop Now</Button></Link>
              <Link to="/products/festival-kits"><Button size="lg" variant="outline" className="border-orange-600 text-orange-600">Festival Kits</Button></Link>
            </div>
          </div>
          <img src="https://images.unsplash.com/photo-1636080810842-2dc5cdfd9483?w=600" alt="Puja" className="rounded-2xl shadow-2xl" />
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-y">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3"><Truck className="text-orange-600" size={32} /><div><p className="font-semibold">Free Shipping</p><p className="text-sm text-gray-500">Above ₹500</p></div></div>
          <div className="flex items-center gap-3"><Shield className="text-orange-600" size={32} /><div><p className="font-semibold">Genuine Products</p><p className="text-sm text-gray-500">100% Authentic</p></div></div>
          <div className="flex items-center gap-3"><Star className="text-orange-600" size={32} /><div><p className="font-semibold">Best Quality</p><p className="text-sm text-gray-500">Premium Items</p></div></div>
          <div className="flex items-center gap-3"><Phone className="text-orange-600" size={32} /><div><p className="font-semibold">Support</p><p className="text-sm text-gray-500">+91 9831072381</p></div></div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-12 bg-orange-50/50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-orange-800 mb-8">Shop by Category</h2>
          {loading ? <div className="flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div> : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {categories.slice(0,12).map(c => (
                <Link key={c.id} to={`/products/${c.slug}`} className="bg-white p-4 rounded-xl shadow-sm hover:shadow-md text-center group">
                  <div className="w-16 h-16 mx-auto mb-3 bg-orange-100 rounded-full overflow-hidden flex items-center justify-center group-hover:bg-orange-200">
                    {c.image_url ? (
                      <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl">🪔</span>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-800 text-sm">{c.name}</h3>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-orange-800">Featured Products</h2>
            <Link to="/products?featured=true" className="text-orange-600 hover:underline">View All →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {featured.map(p => (
              <Card key={p.id} className="group overflow-hidden">
                <Link to={`/product/${p.id}`}>
                  <div className="aspect-square overflow-hidden bg-orange-50">
                    <img src={p.images?.[0] || 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=400'} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition" />
                  </div>
                </Link>
                <CardContent className="p-4">
                  <p className="text-xs text-orange-600 mb-1">{p.category_name}</p>
                  <Link to={`/product/${p.id}`}><h3 className="font-medium text-gray-800 hover:text-orange-600 line-clamp-2">{p.name}</h3></Link>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold text-orange-600">₹{p.sale_price || p.price}</span>
                    <div className="flex gap-1">
                      <WhatsAppOrderButton product={p} compact />
                      <Button size="sm" onClick={() => { addToCart(p); toast.success('Added!'); }} className="bg-orange-600 hover:bg-orange-700 h-9 w-9 p-0" aria-label="Add to cart"><ShoppingCart size={16} /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <WhyChooseUs />
      <Reviews />
      <section className="py-12 bg-gradient-to-r from-orange-600 to-yellow-500 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Shop?</h2>
        <p className="mb-6 text-orange-100">Browse our complete collection of puja samagri</p>
        <Link to="/products"><Button size="lg" variant="secondary" className="bg-white text-orange-600">Explore All Products</Button></Link>
      </section>
    </div>
  );
};
